/**
 * useBetaStatus - Hook for checking beta tester status and app settings
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';

interface BetaTesterStatus {
  total_slots: number;
  approved_count: number;
  pending_count: number;
  remaining_slots: number;
}

export type ActiveLandingPage = 'main' | 'beta' | 'maintenance' | 'coming_soon';

export interface AppSettings {
  active_landing_page: ActiveLandingPage;
  /** Legacy single toggle. True when either maintenance scope is active. */
  maintenance_mode: boolean;
  /** Disables /dashboard/*, /guest/*, /sign-in. Marketing/blog stay online. */
  app_maintenance_mode: boolean;
  /** Disables /, /blog, /pricing, /about, /privacy, etc. App stays online. */
  marketing_maintenance_mode: boolean;
  /** Estimated minutes shown on the maintenance page. */
  maintenance_estimated_minutes: number;
  beta_registration_open: boolean;
}

/**
 * Get current beta tester slots status
 */
export function useBetaTesterStatus() {
  return useQuery({
    queryKey: ['beta-tester-status'],
    queryFn: async (): Promise<BetaTesterStatus> => {
      const { data, error } = await supabase.rpc('get_beta_tester_count');

      if (error) {
        console.error('Error fetching beta tester status:', error);
        // Return default values if function doesn't exist yet
        return {
          total_slots: 20,
          approved_count: 0,
          pending_count: 0,
          remaining_slots: 20,
        };
      }

      return data?.[0] || {
        total_slots: 20,
        approved_count: 0,
        pending_count: 0,
        remaining_slots: 20,
      };
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Get app settings for landing page routing
 */
export function useAppSettings() {
  return useQuery({
    queryKey: ['app-settings'],
    queryFn: async (): Promise<AppSettings> => {
      const settings: AppSettings = {
        active_landing_page: 'main',
        maintenance_mode: false,
        app_maintenance_mode: false,
        marketing_maintenance_mode: false,
        maintenance_estimated_minutes: 30,
        beta_registration_open: true,
      };

      try {
        const [
          landingPageRes,
          legacyMaintenanceRes,
          appMaintenanceRes,
          marketingMaintenanceRes,
          estimatedMinutesRes,
          betaOpenRes,
        ] = await Promise.all([
          supabase.rpc('get_app_setting', { p_key: 'active_landing_page' }),
          supabase.rpc('get_app_setting', { p_key: 'maintenance_mode' }),
          supabase.rpc('get_app_setting', { p_key: 'app_maintenance_mode' }),
          supabase.rpc('get_app_setting', { p_key: 'marketing_maintenance_mode' }),
          supabase.rpc('get_app_setting', { p_key: 'maintenance_estimated_minutes' }),
          supabase.rpc('get_app_setting', { p_key: 'beta_registration_open' }),
        ]);

        if (landingPageRes.data) {
          settings.active_landing_page = landingPageRes.data as ActiveLandingPage;
        }
        if (appMaintenanceRes.data !== null) {
          settings.app_maintenance_mode = appMaintenanceRes.data === true;
        }
        if (marketingMaintenanceRes.data !== null) {
          settings.marketing_maintenance_mode = marketingMaintenanceRes.data === true;
        }
        if (typeof estimatedMinutesRes.data === 'number') {
          settings.maintenance_estimated_minutes = estimatedMinutesRes.data;
        }
        if (betaOpenRes.data !== null) {
          settings.beta_registration_open = betaOpenRes.data === true;
        }

        // Legacy fallback: if old single flag is set but new scoped flags aren't,
        // treat it as full maintenance.
        const legacyOn = legacyMaintenanceRes.data === true;
        settings.maintenance_mode =
          settings.app_maintenance_mode || settings.marketing_maintenance_mode || legacyOn;

        if (legacyOn && !settings.app_maintenance_mode && !settings.marketing_maintenance_mode) {
          settings.app_maintenance_mode = true;
          settings.marketing_maintenance_mode = true;
        }
      } catch (error) {
        console.error('Error fetching app settings:', error);
      }

      return settings;
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // poll every minute so toggles propagate
  });
}

/**
 * Check if a user is a beta tester
 */
export function useIsBetaTester(userId: string | null) {
  const { isReady: authReady } = useAuthReady();

  return useQuery({
    queryKey: ['is-beta-tester', userId],
    queryFn: async (): Promise<{ isBeta: boolean; expiresAt: string | null; isExpired: boolean }> => {
      if (!userId) {
        return { isBeta: false, expiresAt: null, isExpired: false };
      }

      const { data, error } = await getSupabase()
        .from('profiles')
        .select('subscription_type, beta_expires_at, subscription_status')
        .eq('id', userId)
        .single();

      if (error || !data) {
        return { isBeta: false, expiresAt: null, isExpired: false };
      }

      const isBeta = data.subscription_type === 'beta_tester';
      const expiresAt = data.beta_expires_at;
      const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;

      return { isBeta, expiresAt, isExpired };
    },
    enabled: !!userId && authReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get beta progress for display
 */
export function getBetaProgress(status: BetaTesterStatus | undefined): {
  percentage: number;
  remaining: number;
  total: number;
  isFull: boolean;
  isAlmostFull: boolean;
} {
  if (!status) {
    return {
      percentage: 0,
      remaining: 20,
      total: 20,
      isFull: false,
      isAlmostFull: false,
    };
  }

  const { approved_count, remaining_slots, total_slots } = status;
  const percentage = Math.round((approved_count / total_slots) * 100);

  return {
    percentage,
    remaining: remaining_slots,
    total: total_slots,
    isFull: remaining_slots === 0,
    isAlmostFull: remaining_slots <= 5 && remaining_slots > 0,
  };
}

/**
 * useBetaStatus - Check if current user is an approved beta tester
 */
export function useBetaStatus() {
  const { userId, isReady: authReady } = useAuthReady();

  return useQuery({
    queryKey: ['beta-status', userId],
    queryFn: async (): Promise<{
      isApprovedBetaTester: boolean;
      expiresAt: string | null;
      isExpired: boolean;
    }> => {
      if (!userId) {
        return { isApprovedBetaTester: false, expiresAt: null, isExpired: false };
      }

      const { data, error } = await getSupabase()
        .from('profiles')
        .select('subscription_type, beta_expires_at, subscription_status')
        .eq('id', userId)
        .single();

      if (error || !data) {
        return { isApprovedBetaTester: false, expiresAt: null, isExpired: false };
      }

      const isBeta = data.subscription_type === 'beta_tester' && data.subscription_status === 'active';
      const expiresAt = data.beta_expires_at;
      const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;

      return {
        isApprovedBetaTester: isBeta && !isExpired,
        expiresAt,
        isExpired,
      };
    },
    enabled: authReady && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export default useBetaTesterStatus;
